/* ContractsCleanList — Fáze 3 quick smlouvy summary on the Karta tab.
 *
 * Shows up to N contracts as clean rows with:
 *  [type-icon]  Title (provider · stage)            value + monthly hint
 *
 * Theme-aware via CSS vars. Empty state is a calm hint, not a giant card.
 */
import { Briefcase, TrendingUp, Home, Car, Heart, Shield, FileText, PiggyBank, Umbrella, ShieldEllipsis } from "lucide-react";
import SoftSection from "@/components/crm/SoftSection";
import { useTextySmluv } from "@/lib/smlouvy/textySmluv";

/* Texty přes t() v pěti jazycích, částky v měně ZEMĚ TENANTA (CZ → Kč,
 * SK → €) — dřív natvrdo cs-CZ a „Kč“ i u slovenského poradce (majitel
 * 4. 10. 2026: „ano“). `label` níž zůstává jako česká záloha slovníku. */

// Robustnost: `contracts` může přijít jako ne-pole (null/{}/string) nebo s null-položkami.
// [...contracts] nad ne-iterovatelnou hodnotou spadne → asArr vrátí čisté pole objektů.
const asArr = (v) => (Array.isArray(v) ? v.filter((x) => x && typeof x === "object") : []);

export const CONTRACT_META = {
  hypoteka:          { label: "Hypotéka",     Icon: Home,        cssVar: "--cat-c" },
  investice:         { label: "Investice",    Icon: TrendingUp,  cssVar: "--cat-a" },
  penzijko:          { label: "Penze",        Icon: PiggyBank,   cssVar: "--cat-b" },
  pojisteni_zivot:   { label: "Životní poj.", Icon: Heart,       cssVar: "--info" },
  pojisteni_auta:    { label: "Auto poj.",    Icon: Car,         cssVar: "--cat-b" },
  pojisteni_majetek: { label: "Majetek poj.", Icon: Shield,      cssVar: "--info" },
  /* Od 4. 10. 2026 vlastní druhy (dřív spadly do „Jiné“). */
  pojisteni_odpovednost: { label: "Odpovědnost", Icon: Umbrella,   cssVar: "--info" },
  pojisteni_ostatni: { label: "Ostatní poj.", Icon: ShieldEllipsis, cssVar: "--info" },
  zdravotni:         { label: "Zdravotní",    Icon: Heart,       cssVar: "--positive" },
  stavebni:          { label: "Stavební",     Icon: Briefcase,   cssVar: "--cat-a" },
  uver:              { label: "Úvěr",         Icon: FileText,    cssVar: "--cat-c" },
  jine:              { label: "Jiné",         Icon: FileText,    cssVar: "--muted" },
};

export default function ContractsCleanList({ contracts = [], limit = 4, onOpenAll, onOpenContract }) {
  const { t, castka } = useTextySmluv();
  const popisek = (type) => {
    const meta = CONTRACT_META[type] || CONTRACT_META.jine;
    return t(`smlouvaKarta.kratce.${CONTRACT_META[type] ? type : "jine"}`, meta.label);
  };
  const list = asArr(contracts);
  const sorted = [...list].sort((a, b) => (Number(b.total_amount) || 0) - (Number(a.total_amount) || 0));
  const shown = sorted.slice(0, limit);
  const remaining = sorted.length - shown.length;

  const extra =
    list.length > 0 ? (
      <button
        onClick={onOpenAll}
        className="text-drobny font-semibold underline tracking-tight"
        style={{ color: "var(--accent)" }}
        data-testid="contracts-clean-list-open-all"
      >
        {t("smlouvaKarta.prehled.smlouvyPocet", { n: list.length })}
      </button>
    ) : null;

  return (
    <SoftSection title={t("smlouvaKarta.prehled.smlouvy")} icon={Briefcase} extra={extra} testid="contracts-clean-list">
      {list.length === 0 ? (
        <div className="text-drobny py-2" style={{ color: "var(--muted)" }}>
          {t("smlouvaKarta.prehled.zadneSmlouvyZatim")}
        </div>
      ) : (
        <div className="space-y-1">
          {shown.map((ct) => {
            const meta = CONTRACT_META[ct.type] || CONTRACT_META.jine;
            const druh = popisek(ct.type);
            const Icon = meta.Icon;
            const total = Number(ct.total_amount) || 0;
            const monthly = Number(ct.monthly_amount) || 0;
            return (
              <button
                key={ct.id || ct._id || ct.title}
                onClick={() => onOpenContract?.(ct)}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-colors"
                style={{ background: "transparent" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-2)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                data-testid={`contracts-clean-row-${ct.id || ct._id || ct.title}`}
              >
                <span
                  className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                  style={{ background: `color-mix(in srgb, var(${meta.cssVar}) 12%, transparent)`, color: `var(${meta.cssVar})` }}
                >
                  <Icon size={13} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-semibold truncate" style={{ color: "var(--ink)" }}>
                    {ct.title || druh}
                  </div>
                  <div className="text-drobny truncate" style={{ color: "var(--muted)" }}>
                    {[ct.provider, druh, ct.fixation_years ? t("smlouvaKarta.prehled.fixaceLet", { n: ct.fixation_years }) : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-[12px] font-bold tabular-nums" style={{ color: "var(--ink)" }}>
                    {total > 0 ? castka(total) : monthly > 0 ? t("smlouvaKarta.prehled.mesicne", { castka: castka(monthly) }) : "—"}
                  </div>
                  {total > 0 && monthly > 0 && (
                    <div className="text-drobny tabular-nums" style={{ color: "var(--muted)" }}>
                      {t("smlouvaKarta.prehled.mesicne", { castka: castka(monthly) })}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
          {remaining > 0 && (
            <button
              onClick={onOpenAll}
              className="w-full text-drobny font-medium py-1 rounded-md transition-colors"
              style={{ color: "var(--accent)" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--accent-soft)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              data-testid="contracts-clean-show-more"
            >
              {t("smlouvaKarta.prehled.zobrazitDalsi", { n: remaining })}
            </button>
          )}
        </div>
      )}
    </SoftSection>
  );
}
